import * as React from 'react';
import axios, { CancelTokenSource } from 'axios';
import clsx from 'clsx';
import { useHistory } from "react-router-dom";
import { Accordion, AccordionSummary, AccordionDetails, RadioGroup, Radio, makeStyles, Typography, useMediaQuery, ClickAwayListener, Tooltip } from '@material-ui/core';
import { ElementDimensions } from '../../styles/ElementDimensions'
import themePrimary from '../../styles/themePrimary';
import { UserStateContext } from '../../providers/UserStateContextProvider';
import { CheckSummary } from '../../models/common/CheckSummary';
import { CheckOutState, CheckOutStateContext } from '../../providers/CheckOutStateContextProvider';
import MiniButton, { MiniButtonProps } from '../common/Buttons/MiniButton';
import { String_System_View, String_Split_TotalLabel, String_System_GeneralError, String_System_ConnectionError,
    String_Split_SplitByAmountLabel, String_Split_SplitByPercentageLabel, String_Split_SplitEvenlyLabel,
    String_Split_Instruction, String_Split_SplitEvenlyError, String_Split_SplitAmountError, 
    String_Split_SplitPercentageError, String_Split_SplitEvenlyLockToolTip, String_Split_SplitAmountPercentageLockToolTip, 
    String_CheckPaid, 
    String_Split_SplitDemoMessage,
    String_System_Cancel,
    String_System_Confirm,
    String_Split_SplitBySeatLabel,
    String_Split_SplitSeatError} from '../../utilities/StringHelper';
import { CheckFlat } from '../../models/common/CheckFlat';
import { GetCheckForSplitRequest } from '../../models/checkout/GetCheckForSplitRequest';
import { GetCheckForSplitResponse } from '../../models/checkout/GetCheckForSplitResponse';
import { ValidateTransaction } from '../common/ValidateTransaction';
import Loader from '../common/Loader';
import { SplitType } from '../../models/common/SplitType';
import SplitEvenly, { SplitEvenlyCountChangeHandler, SplitEvenlyProps } from './SplitEvenly';
import SplitByAmount, { SplitByAmountChangeHandler, SplitByAmountProps } from './SplitByAmount';
import SplitByPercentage, { SplitByPercentageChangeHandler, SplitByPercentageProps } from './SplitByPercentage';
import { CheckState, CheckStateContext } from '../../providers/CheckStateContextProvider';
import SplitButtons, { SplitButtonProps } from '../common/Buttons/SplitButtons';
import { CheckClaimSummary } from '../../models/common/CheckClaimSummary';
import CustomRadio from '../common/CustomRadio';
import ErrorDialog, { ErrorDialogProps } from '../common/ErrorDialog';
import MessageDialog, { MessageDialogProps } from '../common/MessageDialog';
import ViewCheckDialog, { ViewCheckDialogProps } from '../common/ViewCheckDialog';
import { CheckOutType } from '../../models/common/CheckOutType';
import ShareLink from '../common/ShareLink';
import SignalR, { CheckMessageType, CheckNotificationType, OnNotificationReceivedHandler } from '../common/SignalR';
import { CheckState as CheckStateEnum } from '../../models/common/CheckState';
import { NavigateCheckOutStart } from '../common/NavigateCheckOutStart';
import { ErrorCode } from '../../models/common/ErrorCode';
import { POSType } from '../../models/common/POSType';
import SplitBySeat, { SplitBySeatChangeHandler, SplitBySeatProps } from './SplitBySeat';
import { GetCheckRequest } from '../../models/checkout/GetCheckRequest';
import { GetCheckResponse } from '../../models/checkout/GetCheckResponse';
import { loadDemoCheckData, loadDemoCheckSummaryData } from '../../models/common/DemoData';

const useStyles = makeStyles({
    details: {
        minHeight: '-webkit-fill-available',
        maxHeight: 'calc(100vh - ' + (ElementDimensions.margin20 + ElementDimensions.padding10 + ElementDimensions.appBarHeight + ElementDimensions.footerBarHeight) + 'px)',
        height: 'calc(100vh - 220px)',
        width: '100%',
        overflowY: 'scroll',
        overflowX: 'hidden',
    },
    detailsLandscape: {
        minHeight: '-webkit-fill-available',
        maxHeight: 'calc(100vh - ' + (ElementDimensions.margin10 + ElementDimensions.padding10 + ElementDimensions.footerBarHeightLandscape) + 'px)',
        width: '100%',
        overflowY: 'scroll',
        overflowX: 'hidden',
    },
    footerButtons: {
        position: 'absolute',
        bottom: 0,
        right: 0,
        height: ElementDimensions.footerBarHeight + 'px',
        padding: ElementDimensions.padding10 + 'px ' + '0px ' + ElementDimensions.padding10 + 'px 0px',
        width: '100%',
        borderTop: '2px solid ' + themePrimary.palette.grey[200],
        backgroundColor: themePrimary.palette.primary.contrastText,
    },
    footerButtonsLandscape: {
        position: 'absolute',
        bottom: 0,
        right: 0,
        left: 'calc(100vw *.3)',
        height: ElementDimensions.footerBarHeightLandscape + 'px',
        padding: ElementDimensions.padding10 + 'px ' + '0px ' + ElementDimensions.padding10 + 'px 0px',
        width: 'calc(100vw * .7)',
        borderTop: '2px solid ' + themePrimary.palette.grey[200],
        backgroundColor: themePrimary.palette.primary.contrastText,
    },
    headerContainer: {
        backgroundColor: themePrimary.palette.grey[200],
        padding: ElementDimensions.padding10 + 'px ',
        margin: ElementDimensions.margin10 + 'px',
        borderRadius: '6px',
        display: 'flex',
    },
    headerGrid: {
        display: 'grid',
        gridTemplateColumns: 'calc(70vw - 20px) calc(30vw - 20px)',
        marginLeft: 'auto',
        marginRight: 'auto',
    },
    headerGridLandscape: {
        display: 'grid',
        gridTemplateColumns: 'calc(30vw - 10px) calc(70vw - 10px)',
        marginLeft: 'auto',
        marginRight: 'auto',
    },
    instructionText: {
        textAlign: 'center',
        marginLeft: ElementDimensions.margin5 + 'px',
        marginRight: ElementDimensions.margin5 + 'px',
        marginBottom: ElementDimensions.margin10 + 'px ',
    },
    loadingContainer: {
        margin: ElementDimensions.margin40,
        textAlign: 'center',
    },
    root: {
        height: '100%',
        overflow: 'hidden',
    },
    rootLandscape: {
        marginTop: '0',
    },
    splitLabel: {
        marginLeft: ElementDimensions.margin10 + 'px',
    },
    totalLabel: {
        marginTop: 'auto',
        marginBottom: 'auto',
    },
    viewCheckButton: {
        textAlign: 'right',
    },
});

const Split = () => {
    let history = useHistory();
    const [checkSummary, setCheckSummary] = React.useState<CheckSummary>(history.location.state);
    const [seatCheckSummaries, setSeatCheckSummaries] = React.useState<CheckSummary[]>([]);
    const [check, setCheck] = React.useState<CheckFlat | null>(null);  
    const [checkClaimSummary, setCheckClaimSummary] = React.useState<CheckClaimSummary | null>(null); 
    const [errorDialogOpen, setErrorDialogOpen] = React.useState<boolean>(false);
    const [errorMessage, setErrorMessage] = React.useState<string>('');
    const [errorDialogTryAgain, setErrorDialogTryAgain] = React.useState<boolean>(true);
    const [loadError, setLoadError] = React.useState<boolean>(false);
    const [loaded, setLoaded] = React.useState<boolean>(false);
    const [splitComplete, setSplitComplete] = React.useState<boolean>(false);
    const isPortrait = true;
    const userState = React.useContext(UserStateContext);
    const checkOutState = React.useContext(CheckOutStateContext);
    const classes = useStyles({ primary_main_color: checkOutState.state.skin.primary_main_color, 
        primary_light_color: checkOutState.state.skin.primary_light_color, primary_dark_color: checkOutState.state.skin.primary_dark_color });
    const checkState = React.useContext(CheckStateContext);
    const [splitType, setSplitType] = React.useState<SplitType>(SplitType.None);
    const [splitAmount, setSplitAmount] = React.useState<number>(0);
    const [splitPercentage, setSplitPercentage] = React.useState<number>(0);
    const [splitEvenlyCount, setSplitEvenlyCount] = React.useState<number>(2);
    const [splitSeatIndex, setSplitSeatIndex] = React.useState<number>(-1);
    const [splitSeatCheckDetail, setSplitSeatCheckDetail] = React.useState<CheckFlat | undefined>(undefined);
    const [viewCheckDialogOpen, setViewCheckDialogOpen] = React.useState<boolean>(false);
    const [noUnclaimedAmountsError, setNoUnclaimedAmountsError] = React.useState<boolean>(false);
    const [checkPaidError, setCheckPaidError] = React.useState<boolean>(false);
    const [isSplitEvenly, setIsSplitEvenly] = React.useState<boolean>(false);
    const [isSplitBySeat, setIsSplitBySeat] = React.useState<boolean>(false);
    const [isAmountOrPercentSplit, setIsAmountOrPercentSplit] = React.useState<boolean>(false);
    const [toolTipEvenlyOpen, setToolTipEvenlyOpen] = React.useState<boolean>(false);
    const [toolTipAmountOpen, setToolTipAmountOpen] = React.useState<boolean>(false);
    const [toolTipPercentageOpen, setToolTipPercentageOpen] = React.useState<boolean>(false);
    const [demoPaymentMessageShow, setDemoPaymentMessageShow] = React.useState<boolean>(false);

    const signalRNotificationReceivedHandler = (type: CheckNotificationType, amount: number) => {
        if (type === CheckNotificationType.AmountClaimed) {
            loadData(axios.CancelToken.source());
        }
        else if (type === CheckNotificationType.PaymentCompleted) {
            loadData(axios.CancelToken.source());
        } 
        else if (type === CheckNotificationType.CheckClosed) {
            loadData(axios.CancelToken.source());
        }
        else if (type === CheckNotificationType.ClaimExpired) {
            loadData(axios.CancelToken.source());
        }
    }

    React.useEffect(() => {
        const cancelToken = axios.CancelToken;
        const source = cancelToken.source();

        const newCheckState: CheckState = checkState.state;
        newCheckState.paymentId = null;                    
        checkState.setState(newCheckState);

        if (checkOutState.state.checkOutType === CheckOutType.Demo) {
                    loadDemoData();
                }
                else {
                    loadData(source); 
                }   
        
        return () => {
            source.cancel();
        };
    }, []);

    const loadData = async (cancelTokenSource: CancelTokenSource) => {
        setLoadError(false);
        setLoaded(false);

        var checkExternalId = checkState.state.checkSummary!!.external_id;
        if (checkOutState.state.posType === POSType.Clear) {
            var allCheck = checkOutState.state.checks!!.find(c => c.is_all_check === true);
            if (allCheck !== null) {
                checkExternalId = allCheck!!.external_id;
            }
        }

        const request: GetCheckForSplitRequest = {
            user_id: userState.state.userId!!,
            check_please_payment_id: checkState.state.paymentId,
            table_id: checkOutState.state.tableId,
        };

        let url: string | undefined = process.env.REACT_APP_CHECKOUT_API_URL + "/location/" + checkOutState.state.restaurantId + "/checks/" + checkState.state.checkSummary!!.id + "/split"

        const headers = {
            'X-API-Key': checkOutState.state.restaurantApiKey,
            'location_id': checkOutState.state.restaurantId
        }

        axios.post(url, request, { headers, cancelToken: cancelTokenSource.token })
        .then(function(response) {
            let r = response.data as GetCheckForSplitResponse;

            if (r.success === true) {
                
                if (r.summary.check_state !== CheckStateEnum.Paid) {
                    if (r.summary.unclaimed_amount === 0) {
                        setNoUnclaimedAmountsError(true);
                    }
                    else {
                        setNoUnclaimedAmountsError(false);
                    }

                    const newCheckState: CheckState = checkState.state;
                    newCheckState.checkSummary = r.summary;
                    newCheckState.checkDetail = r.detail;
                    
                    checkState.setState(newCheckState);

                    if (r.claim_summary.split_evenly_count !== null) {
                        setSplitEvenlyCount(r.claim_summary.split_evenly_count);

                        if (r.claim_summary.split_evenly_count > 1) {
                            setSplitType(SplitType.SplitEvenly);
                            setIsSplitEvenly(true);
                            setIsAmountOrPercentSplit(false);
                            setSplitComplete(true);
                        }
                    }
                    else if (r.claim_summary.amount_claimed > 0 || r.claim_summary.percent_claimed > 0) {
                        setIsSplitEvenly(false);
                        setIsAmountOrPercentSplit(true);
                    }
                    else {
                        setIsSplitEvenly(false);
                        setIsAmountOrPercentSplit(false);
                    }

                    setIsSplitBySeat(r.claim_summary.is_split_by_seat);
                    setCheckSummary(r.summary);
                    setSeatCheckSummaries(r.seat_check_summaries);
                    setCheck(r.detail);
                    setCheckClaimSummary(r.claim_summary);
                }
                else {
                    setCheckPaidError(true);
                    setErrorDialogTryAgain(false);
                    setErrorMessage(String_CheckPaid.find(s => s.key === userState.state.locale)!!.value);
                    setErrorDialogOpen(true);
                }
            }
            else {
                setErrorMessage(String_System_GeneralError.find(s => s.key === userState.state.locale)!!.value);
                setErrorDialogTryAgain(true);
                setErrorDialogOpen(true);
                setLoadError(true);
            }

            setLoaded(true);
        })
        .catch(function(error) {
            if (error.response) {
                setErrorMessage(String_System_GeneralError.find(s => s.key === userState.state.locale)!!.value);
            }
            else {
                setErrorMessage(String_System_ConnectionError.find(s => s.key === userState.state.locale)!!.value);
            }

            setErrorDialogTryAgain(true);
            setErrorDialogOpen(true);
            setLoaded(true);
            setLoadError(true);
        });
    };

    const loadDemoData = () => {
        var checkSummary: CheckSummary = loadDemoCheckSummaryData()
        var check: CheckFlat = loadDemoCheckData()
        var claimSummary: CheckClaimSummary = {
            amount_claimed: 0,
            percent_claimed: 0,
            amount_unclaimed: 2522,
            amount_unclaimed_string: "$25.22",
            percent_unclaimed: 100,
            percent_unclaimed_string: "100",
            split_evenly_count: null,
            is_split_by_seat: false
        }

        setIsSplitBySeat(false);
        setCheckSummary(checkSummary);
        setSeatCheckSummaries([]);
        setCheck(check);
        setCheckClaimSummary(claimSummary);
        setLoaded(true);
    }

    const handleViewCheckButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setViewCheckDialogOpen(true);
    }

    const viewCheckButtonProps: MiniButtonProps = {
        value:'',
        disabled: false,
        label: String_System_View.find(s => s.key === userState.state.locale)!!.value,
        onClickHandler: handleViewCheckButtonClick,
    }

    const handleSplitTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.value === 'amount') {
            setSplitType(SplitType.SplitByAmount);
        }
        else if (event.target.value === 'percent') {
            setSplitType(SplitType.SplitByPercentage);
        }
        else if (event.target.value === 'even') {
            setSplitComplete(true);
            setSplitType(SplitType.SplitEvenly);
        }
        else if (event.target.value === 'seat') {
            setSplitType(SplitType.SplitBySeat);
        }
        else {
            setSplitType(SplitType.None);
        }
      };

    const getSplitTypeString = (): string => {
        if (splitType === SplitType.SplitByAmount) {
            return 'amount';
        }
        else if (splitType === SplitType.SplitByPercentage) {
            return 'percent';
        }
        else if (splitType === SplitType.SplitEvenly) {
            return 'even';
        }
        else if (splitType === SplitType.SplitBySeat) {
            return 'seat';
        }
        else {
            return 'none';
        }
    }

    const handleSplitByAmountChange: SplitByAmountChangeHandler = (newValue: number) => {
        setSplitAmount(newValue);

        if (splitType === SplitType.SplitByAmount && newValue > 0) {
            setSplitComplete(true);
        }
    }

    const splitByAmountProps: SplitByAmountProps = {
        checkSummary: checkSummary,
        claimSummary: checkClaimSummary,
        onChangeHandler: handleSplitByAmountChange,
    }

    const handleSplitByPercentageChange: SplitByPercentageChangeHandler = (newValue: number) => {
        setSplitPercentage(newValue);

        if (splitType === SplitType.SplitByPercentage && newValue > 0) {
            setSplitComplete(true);
        }
    }

    const splitByPercentageProps: SplitByPercentageProps = {
        checkSummary: checkSummary,
        claimSummary: checkClaimSummary,
        onChangeHandler: handleSplitByPercentageChange,
    }

    const handleSplitEvenlyCountChange: SplitEvenlyCountChangeHandler = (newValue: number) => {
        setSplitEvenlyCount(newValue);

        if (splitType === SplitType.SplitEvenly && newValue > 1) {
            setSplitComplete(true);
        }
    }

    const splitEvenlyProps: SplitEvenlyProps = {
        claimSummary: checkClaimSummary,
        onCountChangeHandler: handleSplitEvenlyCountChange,
    }

    const handleSplitBySeatChange: SplitBySeatChangeHandler = (index: number) => {
        setSplitSeatIndex(index);

        if (splitType === SplitType.SplitBySeat) {
            setSplitComplete(true);
        }
    }

    const splitBySeatProps: SplitBySeatProps = {
        checkSummaries: seatCheckSummaries,
        onChangeHandler: handleSplitBySeatChange
    }

    const getCheckForSeatSplit = async (cancelTokenSource: CancelTokenSource, check: CheckSummary) => {

        if (check !== undefined) {
            const request: GetCheckRequest = {
                user_id: userState.state.userId!!,
                use_claimed_amounts: true,
                release_claims: false,
            };        
    
            let url: string | undefined = process.env.REACT_APP_CHECKOUT_API_URL + "/location/" + checkOutState.state.restaurantId + "/checks/" + check.id
    
            const headers = {
                'X-API-Key': checkOutState.state.restaurantApiKey,
                'location_id': checkOutState.state.restaurantId
            }
    
            axios.post(url, request, { headers, cancelToken: cancelTokenSource.token })
            .then(function(response) {
                let r = response.data as GetCheckResponse;
    
                if (r.success === true) {
                    const newCheckState: CheckState = checkState.state;
                    newCheckState.checkSummary = check
                    newCheckState.checkDetail = r.detail
                    newCheckState.splitAmount = 0;
                    newCheckState.splitType = SplitType.SplitBySeat;
                    newCheckState.splitEvenlyCount = 0;
                    newCheckState.splitPercentage = 0;
                    newCheckState.paymentId = null;
                    checkState.setState(newCheckState);

                    history.push('/payment/pay');
                }
                else {
                    setErrorMessage(String_System_GeneralError.find(s => s.key === userState.state.locale)!!.value);
                    setErrorDialogOpen(true);
                }
            })
            .catch(function(error) {
                if (error.response) {
                    setErrorMessage(String_System_GeneralError.find(s => s.key === userState.state.locale)!!.value);
                }
                else {
                    setErrorMessage(String_System_ConnectionError.find(s => s.key === userState.state.locale)!!.value);
                }
                
                setErrorDialogOpen(true);
            });
        }
    }

    const handleSplitLeftButtonClick = (event: React.MouseEvent<HTMLElement>) => {

        history.push({
            pathname: '/checkout/check',
            state: { 
                checkSummary: checkState.state.checkSummary,
            }
        });    
    }

    const handleSplitRightButtonClick = (event: React.MouseEvent<HTMLElement>) => {
        if (checkOutState.state.checkOutType === CheckOutType.Demo) {
            setDemoPaymentMessageShow(true);
        }
        else {
            if (validateSplits()) {
                if (splitType !== SplitType.SplitBySeat) {
                    const newCheckState: CheckState = checkState.state;

                    newCheckState.splitType = splitType;
                    newCheckState.splitAmount = splitAmount;
                    newCheckState.splitPercentage = splitPercentage;
                    newCheckState.splitEvenlyCount = splitEvenlyCount;
    
                    checkState.setState(newCheckState);
       
                    history.push('/payment/pay');
                }
                else {
                    getCheckForSeatSplit(axios.CancelToken.source(), seatCheckSummaries[splitSeatIndex])
                }
            }
            else {
                setLoadError(false);
                setErrorDialogTryAgain(false);
                setErrorDialogOpen(true);
            }
        }
    }

    const splitButtonProps: SplitButtonProps = {
        leftLabel: String_System_Cancel.find(s => s.key === userState.state.locale)!!.value,
        rightLabel: String_System_Confirm.find(s => s.key === userState.state.locale)!!.value,
        leftDisabled: false,
        rightDisabled: !splitComplete || noUnclaimedAmountsError,
        showIcons: false,
        useForwardIcon: false,
        useBackIcon: false,
        onLeftClickHandler: handleSplitLeftButtonClick,
        onRightClickHandler: handleSplitRightButtonClick,
    }

    const handleAccordionChange = (value: string) => (event: React.ChangeEvent<{}>, isExpanded: boolean) => {
        if (isExpanded) {
            if (value === 'amount' && !isSplitEvenly) {
                if (splitAmount === 0) {
                    setSplitComplete(false);
                }
                
                setSplitType(SplitType.SplitByAmount);
            }
            else if (value === 'percent'  && !isSplitEvenly) {
                if (splitPercentage === 0) {
                    setSplitComplete(false);
                }

                setSplitType(SplitType.SplitByPercentage);
            }
            else if (value === 'seat') {
                if (splitSeatIndex === -1) {
                    setSplitComplete(false);
                }

                setSplitType(SplitType.SplitBySeat);
            }
            else if (value === 'even' && !isAmountOrPercentSplit) {
                setSplitComplete(true);
                setSplitType(SplitType.SplitEvenly);
            }
        }
    };

    const handleDivAccordionClick = (value: string) => (event: React.MouseEvent<{}>) => {
        if (value === 'amount') {
            if (isSplitEvenly) {
                handleTooltipAmountOpen();
            }                
        }
        else if (value === 'percent') {
            if (isSplitEvenly) {
                handleTooltipPercentageOpen();
            }                 
        }
        else if (value === 'even') {
            if (isAmountOrPercentSplit) {
                handleTooltipEvenlyOpen();
            }                                 
        }
    }

    const validateSplits = (): boolean => {
        if (splitType === SplitType.SplitEvenly) {
            if (splitEvenlyCount < 2) {
                setErrorMessage(String_Split_SplitEvenlyError.find(s => s.key === userState.state.locale)!!.value);
                return false;
            }
        }
        else if (splitType === SplitType.SplitByAmount) {
            if (splitAmount > checkClaimSummary!!.amount_unclaimed) {
                setErrorMessage(String_Split_SplitAmountError.find(s => s.key === userState.state.locale)!!.value);
                return false;
            }
        }
        else if (splitType === SplitType.SplitByPercentage) {
            if (splitPercentage > checkClaimSummary!!.percent_unclaimed) {
                setErrorMessage(String_Split_SplitPercentageError.find(s => s.key === userState.state.locale)!!.value);
                return false;
            }
        }
        else if (splitType === SplitType.SplitBySeat) {
            if (splitSeatIndex === -1) {
                setErrorMessage(String_Split_SplitSeatError.find(s => s.key === userState.state.locale)!!.value);
                return false;
            }
        }

        return true;
    }

    const handleMessageDialogClose = (event: React.MouseEvent<HTMLElement>) => {
        history.push('/payment/pay');
    }

    const messageDialogProps: MessageDialogProps = {
        open: demoPaymentMessageShow,
        message: String_Split_SplitDemoMessage.find(s => s.key === userState.state.locale)!!.value,
        onCloseHandler: handleMessageDialogClose,
    }

    const handleErrorDialogClose = (event: React.MouseEvent<HTMLElement>) => {
        if (loadError) {
            setErrorDialogOpen(false);
            loadData(axios.CancelToken.source());
        }
        else if (checkPaidError) {
            if (checkOutState.state.checkOutType === CheckOutType.TextToPay || checkOutState.state.checkOutType === CheckOutType.GrabNGo || 
                checkOutState.state.checkOutType === CheckOutType.ScanToPay || checkOutState.state.checkOutType === CheckOutType.ScanReceiptToPay) {
                    NavigateCheckOutStart();
            }
            else {
                setErrorDialogOpen(false);    
            }
        }
        else if (demoPaymentMessageShow) {
            history.push('/payment/pay');
        }
        else {
            setErrorDialogOpen(false);
        }
    }

    const errorDialogProps: ErrorDialogProps = {
        open: errorDialogOpen,
        message: errorMessage,
        tryAgain: errorDialogTryAgain,
        onCloseHandler: handleErrorDialogClose,
    }

    const handleViewCheckPayButtonClick = (event: React.MouseEvent<HTMLElement>, check: CheckSummary) => {
        
    }

    const handleViewCheckDialogCloseClick = (event: React.MouseEvent<HTMLElement>) => {
        
        setViewCheckDialogOpen(false);
    }

    const viewCheckDialogProps: ViewCheckDialogProps = {
        checkSummary: checkSummary!!,
        checkDetail: check!!,
        open: viewCheckDialogOpen,
        showFooter: false,
        useClaimedAmounts: true,
        onPayClickHandler: handleViewCheckPayButtonClick,
        onCloseHandler: handleViewCheckDialogCloseClick,
    }

    const signalRInitializedHandler = () => {
        
    }

    const handleTooltipEvenlyClose = () => {
        setTimeout(() => {setToolTipEvenlyOpen(false);}, 2000);
    };

    const handleTooltipEvenlyOpen = () => {
        setToolTipEvenlyOpen(true);
    };

    const handleTooltipAmountClose = () => {
        setTimeout(() => {setToolTipAmountOpen(false);}, 2000);
    };

    const handleTooltipAmountOpen = () => {
        setToolTipAmountOpen(true);
    };

    const handleTooltipPercentageClose = () => {
        setTimeout(() => {setToolTipPercentageOpen(false);}, 2000);
    };

    const handleTooltipPercentageOpen = () => {
        setToolTipPercentageOpen(true);
    };

    return (
        <div className={clsx(classes.root, {
            [classes.rootLandscape]: !isPortrait,
        })}>

            {!loaded && !loadError &&
                <div className={classes.loadingContainer}>
                    {!loaded &&
                        <Loader />                    
                    }
                </div>
            }

            {loaded && !loadError && check !== null && check !== undefined && 
                checkOutState.state.checkOutType !== CheckOutType.ScanToPay &&
                checkOutState.state.checkOutType !== CheckOutType.ScanReceiptToPay &&
                checkOutState.state.checkOutType !== CheckOutType.Demo &&
                <div>
                    <ShareLink />
                </div>
            }

            <div className={isPortrait === true ? classes.details : classes.detailsLandscape}>
                {loaded && !loadError && check !== null && check !== undefined &&
                    <div className={classes.headerContainer}>
                        <div className={isPortrait ? classes.headerGrid : classes.headerGridLandscape}>
                            <Typography variant="h6" className={classes.totalLabel}>
                                {String_Split_TotalLabel.find(s => s.key === userState.state.locale)!!.value} {checkSummary.total_with_service_charge_string}
                            </Typography>
                            <div className={classes.viewCheckButton}>
                                <MiniButton
                                    value={checkSummary.id.toString()}
                                    disabled={viewCheckButtonProps.disabled}
                                    label={viewCheckButtonProps.label}
                                    onClickHandler={viewCheckButtonProps.onClickHandler} />
                            </div>
                        </div>
                    </div>
                }

                {loaded && !loadError && check !== null && check !== undefined &&
                    <Typography variant="h6" className={classes.instructionText}>
                        {String_Split_Instruction.find(s => s.key === userState.state.locale)!!.value}
                    </Typography>
                }

                {loaded && !loadError && check !== null && check !== undefined &&
                    <div>
                        <RadioGroup value={getSplitTypeString()} onChange={handleSplitTypeChange}>

                            {checkOutState.state.posType === POSType.Clear && seatCheckSummaries.length > 0 &&
                            
                                <div onClick={handleDivAccordionClick('seat')}>
                                    <Accordion 
                                        expanded={splitType === SplitType.SplitBySeat} 
                                        onChange={handleAccordionChange('seat')}
                                        disabled={isSplitEvenly || isAmountOrPercentSplit}>
                                        <AccordionSummary>
                                            <CustomRadio 
                                                value="seat"  />
                                            <Typography variant="h6" className={classes.splitLabel}>
                                                {String_Split_SplitBySeatLabel.find(s => s.key === userState.state.locale)!!.value}
                                            </Typography>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <SplitBySeat 
                                                checkSummaries={splitBySeatProps.checkSummaries}
                                                onChangeHandler={splitBySeatProps.onChangeHandler} />
                                        </AccordionDetails>
                                    </Accordion>
                                </div>

                            }

                            <ClickAwayListener onClickAway={handleTooltipEvenlyClose}>
                                <Tooltip
                                    arrow
                                    disableHoverListener
                                    disableFocusListener
                                    onClose={handleTooltipEvenlyClose}
                                    open={toolTipEvenlyOpen}
                                    title={String_Split_SplitAmountPercentageLockToolTip.find(s => s.key === userState.state.locale)!!.value}>
                                    <div onClick={handleDivAccordionClick('even')}>
                                        <Accordion 
                                            expanded={splitType === SplitType.SplitEvenly} 
                                            onChange={handleAccordionChange('even')}
                                            disabled={isSplitBySeat || isAmountOrPercentSplit}>
                                            <AccordionSummary>
                                                <CustomRadio 
                                                    value="even"  />
                                                <Typography variant="h6" className={classes.splitLabel}>
                                                    {String_Split_SplitEvenlyLabel.find(s => s.key === userState.state.locale)!!.value}
                                                </Typography>
                                            </AccordionSummary>
                                            <AccordionDetails>
                                                <SplitEvenly 
                                                    claimSummary={splitByAmountProps.claimSummary}
                                                    onCountChangeHandler={splitEvenlyProps.onCountChangeHandler} />
                                            </AccordionDetails>
                                        </Accordion>
                                    </div>
                                </Tooltip>
                            </ClickAwayListener>

                            <ClickAwayListener onClickAway={handleTooltipAmountClose}>
                                <Tooltip
                                    arrow
                                    disableHoverListener
                                    disableFocusListener
                                    onClose={handleTooltipAmountClose}
                                    open={toolTipAmountOpen}
                                    title={String_Split_SplitEvenlyLockToolTip.find(s => s.key === userState.state.locale)!!.value}>
                                    <div onClick={handleDivAccordionClick('amount')}>
                                        <Accordion 
                                            expanded={splitType === SplitType.SplitByAmount} 
                                            onChange={handleAccordionChange('amount')}
                                            disabled={isSplitBySeat || isSplitEvenly}>
                                            <AccordionSummary>
                                                <CustomRadio 
                                                    value="amount"  />
                                                <Typography variant="h6" className={classes.splitLabel}>
                                                    {String_Split_SplitByAmountLabel.find(s => s.key === userState.state.locale)!!.value}
                                                </Typography>
                                            </AccordionSummary>
                                            <AccordionDetails>
                                                <SplitByAmount 
                                                    checkSummary={splitByAmountProps.checkSummary}
                                                    claimSummary={splitByAmountProps.claimSummary}  
                                                    onChangeHandler={splitByAmountProps.onChangeHandler} />
                                            </AccordionDetails>
                                        </Accordion>
                                    </div>
                                </Tooltip>
                            </ClickAwayListener>
                            
                            <ClickAwayListener onClickAway={handleTooltipPercentageClose}>
                                <Tooltip
                                    arrow
                                    disableHoverListener
                                    disableFocusListener
                                    onClose={handleTooltipPercentageClose}
                                    open={toolTipPercentageOpen}
                                    title={String_Split_SplitEvenlyLockToolTip.find(s => s.key === userState.state.locale)!!.value}>
                                    <div onClick={handleDivAccordionClick('percent')}>
                                        <Accordion 
                                            expanded={splitType === SplitType.SplitByPercentage} 
                                            onChange={handleAccordionChange('percent')}
                                            disabled={isSplitBySeat || isSplitEvenly}>
                                            <AccordionSummary>
                                                <CustomRadio 
                                                    value="percent" />
                                                <Typography variant="h6" className={classes.splitLabel}>
                                                    {String_Split_SplitByPercentageLabel.find(s => s.key === userState.state.locale)!!.value}
                                                </Typography>
                                            </AccordionSummary>
                                            <AccordionDetails>
                                                <SplitByPercentage 
                                                    checkSummary={splitByPercentageProps.checkSummary} 
                                                    claimSummary={splitByAmountProps.claimSummary}  
                                                    onChangeHandler={splitByPercentageProps.onChangeHandler} />
                                            </AccordionDetails>
                                        </Accordion>
                                    </div>
                                </Tooltip>
                            </ClickAwayListener>
                        </RadioGroup>
                    </div>
                }

                {loaded && !loadError && check !== null && check !== undefined && 
                    <div className={isPortrait ? classes.footerButtons : classes.footerButtonsLandscape}>
                        <SplitButtons
                            leftLabel={splitButtonProps.leftLabel}
                            rightLabel={splitButtonProps.rightLabel}
                            leftDisabled={splitButtonProps.leftDisabled}
                            rightDisabled={splitButtonProps.rightDisabled}
                            showIcons={splitButtonProps.showIcons}
                            useForwardIcon={splitButtonProps.useForwardIcon}
                            useBackIcon={splitButtonProps.useBackIcon}
                            onLeftClickHandler={splitButtonProps.onLeftClickHandler}
                            onRightClickHandler={splitButtonProps.onRightClickHandler} />
                    </div>
                }
            </div>

            <ViewCheckDialog 
                checkSummary={viewCheckDialogProps.checkSummary}
                checkDetail={viewCheckDialogProps.checkDetail}
                open={viewCheckDialogProps.open}
                showFooter={viewCheckDialogProps.showFooter}
                useClaimedAmounts={viewCheckDialogProps.useClaimedAmounts}
                onPayClickHandler={viewCheckDialogProps.onPayClickHandler}
                onCloseHandler={viewCheckDialogProps.onCloseHandler} />
        
            <ErrorDialog 
                open={errorDialogProps.open}
                message={errorDialogProps.message}
                tryAgain={errorDialogProps.tryAgain}
                onCloseHandler={errorDialogProps.onCloseHandler} />

            <MessageDialog 
                open={messageDialogProps.open}
                message={messageDialogProps.message}
                onCloseHandler={messageDialogProps.onCloseHandler} />

            {checkOutState.state.checks !== null && 
                <SignalR 
                    oldChecks={null}
                    messageType={CheckMessageType.Subscribe}
                    load={true}
                    refreshCheck={true}
                    onNotificationReceived={signalRNotificationReceivedHandler}
                    onInitialized={signalRInitializedHandler} />
            }

        </div>
    );
};

export default Split;